import React from 'react';
import { Users, Heart, Target, Globe, Award, Handshake, Shield, TreePine, Mail, Phone } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';

export const AboutusSection: React.FC = () => {
  const { t } = useLanguage();

  return (
    <section id="about-us" className="relative pt-8 pb-12 bg-gradient-to-br from-gray-50 via-white to-blue-50 overflow-hidden" style={{ scrollMarginTop: '100px' }}>
      {/* Animated background */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-1/4 w-96 h-96 bg-gradient-to-br from-[#22C55E]/10 to-transparent rounded-full blur-3xl animate-float-slow"></div>
        <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-gradient-to-br from-blue-400/10 to-transparent rounded-full blur-2xl animate-float-reverse"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-block bg-white/40 backdrop-blur-md border border-white/30 rounded-full px-6 py-2 mb-6 shadow-lg">
            <span className="text-sm font-medium text-[#22C55E]">Connecting People, Sharing Journeys</span>
          </div>
          <h2 className="text-5xl md:text-7xl font-light text-gray-900 mb-6 animate-slide-up">
            {t('companyLinks.aboutUs', 'footer')}
          </h2>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto">
          <div className="bg-white/30 backdrop-blur-xl rounded-3xl border border-white/40 shadow-xl overflow-hidden">
            <div className="p-6 sm:p-8 lg:p-12 space-y-8">
              
              {/* Mission Section */}
              <section>
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#22C55E] to-[#16A34A] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Duri Journey</h2>
                    
                    <div className="space-y-4 text-gray-700 leading-relaxed">
                      <p>
                        A journey that changed my direction: In March 2025, a routine intercity trip turned into a 11‑hour marathon on a route that normally takes 4.5 hours by car. On that long, reflective ride across public and private buses, I watched countless cars pass by empty seats, solo drivers, unused capacity everywhere. I kept asking myself: If hosts could share these rides, wouldn't we save time, money, and most importantly our planet?
                      </p>

                      <div>
                        <p className="font-semibold text-gray-900 mb-2">That simple, urgent observation became Duri:</p>
                        <p className="font-semibold text-gray-900 mb-2">Why now:</p>
                        <p>
                          Mobility, air quality, and the cost of doing nothing: India's air quality challenges are well‑documented, and our cities shoulder a disproportionate burden. Even modest shifts toward shared mobility can compound into meaningful planetary impact. Our internal modeling suggested that if a Rider‑Host (vehicle owner) hosts ~10,000 rides, carrying a minimum of 2+ guests per ride, the carbon impact savings could be comparable to planting up to ~1 million trees over time depending on real‑world adoption, route lengths, vehicle types, and occupancy. This is an illustrative estimate; actual impact varies by assumptions and local conditions.
                        </p>
                      </div>

                      <p>
                        That estimate humbled me and it compelled our team to turn this into a tangible, community‑scale product.
                      </p>

                      <p>
                        <span className="font-semibold">Introducing Duri Ridesharing:</span> Built by the Community, with HansAI Technosoft LLC (hansaitechnosoft.com) BOT with vCTO Craftsmanship.
                      </p>

                      <p>
                        We designed and developed the Duri Ridesharing App with a singular focus: trust, safety, and inclusivity all delivered through thoughtful engineering and AI assistance.
                      </p>

                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">What sets Duri apart?</h3>
                        <ul className="space-y-3 list-none">
                          <li className="flex items-start gap-3">
                            <span className="text-[#22C55E] font-semibold mt-0.5">1.</span>
                            <span>Vetted identities to build trust</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <span className="text-[#22C55E] font-semibold mt-0.5">2.</span>
                            <span>An AI‑powered assistant for effortless hosting & booking</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <span className="text-[#22C55E] font-semibold mt-0.5">3.</span>
                            <span>Inclusivity by design: reserved seats for women travelers</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <span className="text-[#22C55E] font-semibold mt-0.5">4.</span>
                            <span>Companion‑compatible ride matching</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <span className="text-[#22C55E] font-semibold mt-0.5">5.</span>
                            <span>Transparent, non‑profit seat‑fare suggestions</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <span className="text-[#22C55E] font-semibold mt-0.5">6.</span>
                            <span>SOS & emergency support</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <span className="text-[#22C55E] font-semibold mt-0.5">7.</span>
                            <span>Live tracking & shareable locations</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <span className="text-[#22C55E] font-semibold mt-0.5">8.</span>
                            <span>Multilingual UX and adaptive UI elements</span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <p className="text-right text-gray-600 italic mt-6 pt-4 border-t border-gray-200">
                      - By Mohan Nanduri, Founder & CTO
                    </p>
                  </div>
                </div>
              </section>

              {/* Vision Section */}
              <section>
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Globe className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">About Founder and Company</h2>
                    <p className="text-gray-700 leading-relaxed mb-4">
                    <b>HansAI Technosoft Pvt. Ltd.,</b> established in 2011 and based in Hyderabad, India, and <b>HansAI Technosoft LLC,</b> founded in 2016 and headquartered in Florida, USA, bring over two decades of software development expertise. Our team of professionals has extensive experience across various industries, including Telecom, Payment Processing, Education, Hospitality, Healthcare, Automotive, eCommerce, Music, and Entertainment. We have proudly served clients across Asia, North America and Trinidad & Tobago, delivering tailored solutions to meet diverse business needs.                    </p>
                    <a 
                      href="https://hansaitechnosoft.com/AboutUs" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-[#22C55E] hover:text-[#16A34A] font-semibold transition-colors duration-300 group"
                    >
                      To know more
                      <svg 
                        className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </a>
                  </div>
                </div>
              </section>

        
    
              

            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
